import { AlertTriangle, ShieldCheck, Scale, FileText, Lock, Clock, HelpCircle } from 'lucide-react';
import { LegalPageLayout, TocItem } from '@/components/legal/LegalPageLayout';

const TOC_ITEMS: TocItem[] = [
  { id: 'definicoes', title: '1. Definições Gerais' },
  { id: 'natureza', title: '2. Natureza dos Serviços e Limitação' },
  { id: 'cadastro', title: '3. Cadastro e Segurança da Conta' },
  { id: 'modalidades', title: '4. Modalidades de Atendimento' },
  { id: 'documentos', title: '5. Documentos Digitais e ICP-Brasil' },
  { id: 'pagamentos', title: '6. Pagamentos, Reagendamentos e Cancelamentos' },
  { id: 'afiliados', title: '7. Programa de Indicação e Afiliados' },
  { id: 'propriedade', title: '8. Propriedade Intelectual' },
  { id: 'alteracoes', title: '9. Alterações dos Termos' },
  { id: 'foro', title: '10. Foro e Legislação Aplicável' },
];

export function TermsPage() {
  return (
    <LegalPageLayout
      badge="Jurídico & Compliance"
      title="Termos de Uso e Condições Gerais de Prestação de Serviços"
      lastUpdated="Outubro de 2026"
      tocItems={TOC_ITEMS}
    >
      <div className="p-4 sm:p-5 rounded-2xl bg-neutral-100/80 dark:bg-neutral-900 border border-border/80 text-sm leading-relaxed space-y-3">
        <p>
          Bem-vindo à <strong>Medikei</strong> (doravante denominada &quot;Medikei&quot;, &quot;Plataforma&quot; ou &quot;nós&quot;), acessível por meio do site{' '}
          <a href="https://www.medikei.com.br" className="text-primary font-semibold hover:underline" target="_blank" rel="noreferrer">
            https://www.medikei.com.br
          </a>{' '}
          e seus respectivos subdomínios ou aplicativos.
        </p>
        <p>
          Ao criar uma conta, agendar uma consulta, cadastrar-se como profissional de saúde ou parceiro afiliado, ou navegar em nossos serviços, você declara expressamente que leu, compreendeu e concorda integralmente com estes <strong>Termos de Uso</strong> e com a nossa <strong>Política de Privacidade</strong>. Caso não concorde com qualquer disposição aqui estabelecida, solicitamos que não utilize nossos serviços.
        </p>
      </div>

      {/* 1. Definições Gerais */}
      <section id="definicoes" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" />
          <span>1. Definições Gerais</span>
        </h2>
        <p className="text-muted-foreground">
          Para os fins destes Termos de Uso, aplicam-se as seguintes definições:
        </p>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>Usuário / Paciente:</strong> Pessoa física titular da conta que busca, agenda ou usufrui de serviços de saúde por meio da Plataforma (em nome próprio ou de dependentes legalmente representados).
          </li>
          <li>
            <strong>Profissional de Saúde:</strong> Médico(a) ou profissional habilitado e devidamente inscrito em seu respectivo conselho de classe (CFM/CRM, CRP, etc.) credenciado na Plataforma.
          </li>
          <li>
            <strong>Plataforma:</strong> O ecossistema tecnológico Medikei que opera a intermediação, agendamento, prontuário digital unificado e processamento financeiro.
          </li>
          <li>
            <strong>Teleconsulta:</strong> Atendimento médico e em saúde realizado de forma síncrona por meio de videoconferência criptografada.
          </li>
          <li>
            <strong>Consulta Domiciliar:</strong> Atendimento presencial realizado pelo profissional de saúde no endereço residencial informado pelo Paciente.
          </li>
          <li>
            <strong>Afiliado / Parceiro:</strong> Usuário que participa do programa &quot;Indique e Ganhe&quot;, divulgando links de indicação para remuneração via comissão.
          </li>
        </ul>
      </section>

      {/* 2. Natureza dos Serviços e Limitação de Responsabilidade */}
      <section id="natureza" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <span>2. Natureza dos Serviços e Limitação de Responsabilidade</span>
        </h2>

        <div className="space-y-2">
          <h3 className="font-bold text-base text-foreground">2.1 Papel de Intermediação Tecnológica</h3>
          <p>
            A Medikei atua como uma empresa de tecnologia intermediadora, facilitando a conexão entre Pacientes e Profissionais de Saúde autônomos.
          </p>
          <p>
            A Medikei <strong>não é um plano de saúde</strong>, não presta serviços médicos diretamente e não mantém vínculo de subordinação técnico-assistencial com os profissionais cadastrados.
          </p>
          <p>
            A responsabilidade civil pelo diagnóstico, prescrição, conduta terapêutica, preenchimento de prontuário e cumprimento ético recai de forma exclusiva e indelegável sobre o Profissional de Saúde, observando as normas do Conselho Federal de Medicina (CFM) e legislação aplicável.
          </p>
        </div>

        {/* Box Crítico de Emergência */}
        <div className="p-4 sm:p-5 rounded-2xl bg-rose-500/10 border-2 border-rose-500/30 text-rose-950 dark:text-rose-200 space-y-2">
          <div className="flex items-center gap-2 font-bold text-sm sm:text-base text-rose-600 dark:text-rose-400 uppercase tracking-wide">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>2.2 Urgências e Emergências Médicas — Aviso Crítico</span>
          </div>
          <p className="text-xs sm:text-sm leading-relaxed">
            A Plataforma Medikei <strong>NÃO se destina ao atendimento de emergências ou urgências médicas com risco iminente de morte</strong>. Em situações de parada cardiorrespiratória, dor torácica aguda, dispneia grave, sangramento incontrolável, perda súbita de consciência, acidentes graves ou sintomas agudos similares, o Usuário deve acionar imediatamente o <strong>SAMU (192)</strong> ou procurar o serviço de pronto-socorro hospitalar mais próximo.
          </p>
        </div>
      </section>

      {/* 3. Cadastro e Segurança da Conta */}
      <section id="cadastro" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          <span>3. Cadastro e Segurança da Conta</span>
        </h2>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>3.1.</strong> Para utilizar os recursos de agendamento e prontuário, o Paciente deve fornecer dados verídicos, completos e atualizados (nome completo, CPF válido, data de nascimento, telefone, e-mail e endereço).
          </li>
          <li>
            <strong>3.2.</strong> As credenciais de acesso (login e senha) são de uso estritamente pessoal e intransferível. O Usuário é o único responsável pela guarda de sua senha e pelas atividades realizadas em sua conta.
          </li>
          <li>
            <strong>3.3.</strong> O atendimento de crianças e adolescentes (menores de 18 anos) deve ser agendado e acompanhado por um responsável legal, o qual deve estar presente durante a consulta (seja teleconsulta ou domiciliar).
          </li>
        </ul>
      </section>

      {/* 4. Modalidades de Atendimento */}
      <section id="modalidades" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          <span>4. Modalidades de Atendimento</span>
        </h2>

        <div className="space-y-2">
          <h3 className="font-bold text-base text-foreground">4.1 Telemedicina (Consultas por Vídeo e Plantão 24h)</h3>
          <p>
            As teleconsultas cumprem as diretrizes da Lei nº 14.510/2022 e da Resolução CFM nº 2.314/2022. É responsabilidade do Usuário dispor de conexão de internet estável, câmera e microfone funcionais.
          </p>
          <p>
            Caso o profissional avalie que o quadro clínico do paciente requer exame físico presencial insubstituível, ele orientará o paciente a buscar atendimento presencial ou serviço de emergência, registrando a conduta em prontuário.
          </p>
          <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-950 dark:text-amber-200 text-xs sm:text-sm">
            <strong>Proibição de Gravação:</strong> Fica expressamente vedada a gravação de áudio, vídeo ou captura de telas da consulta sem o consentimento formal e mútuo prévio entre o profissional e o paciente.
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <h3 className="font-bold text-base text-foreground">4.2 Consultas Domiciliares (Médico em Casa)</h3>
          <p>
            O paciente deve garantir condições básicas de higiene, iluminação, segurança e contenção de animais domésticos para a realização da consulta no local indicado.
          </p>
          <p>
            O profissional de saúde reserva-se o direito de não adentrar ou de interromper o atendimento caso identifique situação de risco iminente à sua integridade física ou moral, cabendo à Medikei a apuração imediata do incidente.
          </p>
        </div>
      </section>

      {/* 5. Documentos Digitais e Validação (ICP-Brasil) */}
      <section id="documentos" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <span>5. Documentos Digitais e Validação (ICP-Brasil)</span>
        </h2>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>5.1.</strong> As receitas médicas, atestados, relatórios e pedidos de exames gerados na Plataforma são assinados digitalmente com certificados digitais padrão ICP-Brasil emitidos em nome do profissional médico habilitado.
          </li>
          <li>
            <strong>5.2.</strong> Tais documentos possuem validade jurídica nacional, de acordo com as normas vigentes de telemedicina e da Anvisa, integrando QR Code público para validação de autenticidade por farmácias e laboratórios de todo o Brasil.
          </li>
          <li>
            <strong>5.3.</strong> A emissão de atestados e medicamentos controlados segue a estrita autonomia e juízo clínico do médico, não havendo garantia de emissão indiscriminada pelo simples pagamento da consulta.
          </li>
        </ul>
      </section>

      {/* 6. Pagamentos, Reagendamentos e Cancelamentos */}
      <section id="pagamentos" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" />
          <span>6. Pagamentos, Reagendamentos e Cancelamentos</span>
        </h2>

        <div className="space-y-2">
          <h3 className="font-bold text-base text-foreground">6.1 Modelo de Cobrança</h3>
          <p>
            A Medikei opera no modelo <em>pay-per-use</em> (pague pelo que usar). Não há taxa de anuidade ou mensalidade obrigatória para pacientes. Os valores das consultas são exibidos de forma clara antes da confirmação do agendamento, processados com segurança via PIX ou Cartão de Crédito.
          </p>
        </div>

        <div className="space-y-2 pt-2">
          <h3 className="font-bold text-base text-foreground">6.2 Cancelamento pelo Paciente</h3>
          <p>
            <strong>Cancelamento com antecedência:</strong> Cancelamentos solicitados com pelo menos 2 (duas) horas de antecedência do horário agendado terão reembolso integral ou concessão de crédito para reagendamento.
          </p>
          <p>
            <strong>Não comparecimento (No-show):</strong> Caso o paciente não acesse a sala virtual no prazo de 15 minutos de tolerância após o horário marcado, ou não esteja presente no endereço na consulta domiciliar, o valor da consulta não será reembolsado, servindo para indenizar a reserva de agenda do profissional.
          </p>
        </div>

        <div className="space-y-2 pt-2">
          <h3 className="font-bold text-base text-foreground">6.3 Cancelamento ou Atraso pelo Profissional</h3>
          <p>
            Em caso de imprevisto justificado do profissional de saúde que impeça o atendimento, o paciente terá direito a reagendamento prioritário ou reembolso integral e imediato via PIX ou estorno no cartão de crédito.
          </p>
        </div>
      </section>

      {/* 7. Programa de Indicação e Afiliados */}
      <section id="afiliados" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <span>7. Programa de Indicação e Afiliados (&quot;Indique e Ganhe&quot;)</span>
        </h2>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>7.1.</strong> O programa permite a usuários e parceiros cadastrados gerar links de indicação e receber comissões pelas consultas concluídas e pagas por seus indicados.
          </li>
          <li>
            <strong>7.2.</strong> O repasse das comissões é efetuado via PIX, mediante solicitação de saque no painel do parceiro após validação de conformidade da indicação.
          </li>
          <li>
            <strong>7.3. Condutas Proibidas:</strong> É proibido o uso de técnicas de SPAM, publicidade enganosa, criação de contas falsas ou autoindicação fraudulenta para burla do sistema. A Medikei reserva-se o direito de cancelar cadastros e reter saldos obtidos mediante fraude comprovada.
          </li>
        </ul>
      </section>

      {/* 8. Propriedade Intelectual */}
      <section id="propriedade" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <span>8. Propriedade Intelectual</span>
        </h2>
        <p>
          Todo o conteúdo disponível no site e aplicativos da Medikei (marcas, logotipos, layouts, interfaces, códigos-fonte, textos e gráficos) é de titularidade exclusiva da Medikei Saúde Digital ou licenciado por esta, estando protegido pela legislação de direitos autorais e propriedade industrial. Qualquer reprodução, engenharia reversa ou distribuição não autorizada é estritamente proibida.
        </p>
      </section>

      {/* 9. Alterações dos Termos */}
      <section id="alteracoes" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          <span>9. Alterações dos Termos</span>
        </h2>
        <p>
          A Medikei poderá revisar e atualizar estes Termos periodicamente para adequação legal, regulatória do CFM ou inclusão de novas funcionalidades. Mudanças materiais serão notificadas por e-mail ou aviso em destaque na plataforma. O uso contínuo após as alterações constitui aceitação tácita dos novos termos.
        </p>
      </section>

      {/* 10. Foro e Legislação Aplicável */}
      <section id="foro" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-primary" />
          <span>10. Foro e Legislação Aplicável</span>
        </h2>
        <p>
          Estes Termos são regidos pelas leis da República Federativa do Brasil. Para dirimir quaisquer litígios oriundos do uso da Plataforma, fica eleito o Foro da Comarca da sede da Medikei, com expressa renúncia a qualquer outro, por mais privilegiado que seja ou venha a ser.
        </p>
      </section>
    </LegalPageLayout>
  );
}
